// The biometric lock must COVER the app, not REPLACE it.
//
// Until 2026-10-21 the lock was `if (bioLocked) return <BiometricLockScreen/>`, which
// swaps the whole render tree for the lock screen. Every page unmounted, so after the
// 2-minute re-lock each page came back with its own useState gone: scroll, open record,
// filters, tab, loaded pages. App-level state (page id, session) survived, which is why
// the owner saw "same page, still logged in, reset to the top".
//
// After the first unlock the tree stays mounted and the lock screen sits on top of it.
// The mounted tree is made unreachable, not removed:
//   visibility:hidden  - nothing paints and nothing takes a touch, but layout is kept,
//                        so window scroll position is not clamped to 0 (display:none would)
//   inert              - no focus, no assistive-tech access
//   aria-hidden        - belt and braces for readers that ignore inert
//
// A COLD launch keeps the old behaviour: nothing has been shown yet, there is no state to
// keep, and mounting pages behind the lock would fetch data before the user has passed
// device auth.

// True when the lock should replace the tree (first paint, nothing ever shown).
export function lockReplacesTree(bioLocked, treeShown) {
  return !!bioLocked && !treeShown;
}

// Props for the mounted app root while the lock is up; empty when unlocked, so the
// unlocked tree renders exactly as before.
export function lockedShellProps(bioLocked) {
  if (!bioLocked) return {};
  return { inert: true, "aria-hidden": "true", style: { visibility: "hidden" } };
}

// Is an auth prompt really in flight? The busy flag stops the prompt's own visibility blip
// from re-locking in a loop, but it must not be able to disable the lock for good: every
// native call settles inside the availability timeout (8 s) plus the prompt timeout (60 s),
// so a flag older than BIO_BUSY_MAX_MS is stale and no longer blocks a re-lock or a retry.
export var BIO_BUSY_MAX_MS = 70 * 1000;
export function bioBusyActive(busy, busyAt, now) {
  return !!busy && (now - (busyAt || 0)) < BIO_BUSY_MAX_MS;
}
